import { useState, useEffect, useRef } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useDebounce } from "../../../../hooks/useDebounce";
import { ComposeSearchService } from "../../../../services/compose-search.service";
import { RecipientCriteria, TagFilter, TagItem } from "../../../../services/compose-search.types";
import { GT_COLORS } from "../../../../constants/designTokens.constants";
import { displayName } from "../../../../helpers/displayName";

// === Props ===

interface Props {
    criteria: RecipientCriteria;
    onAddTagFilter: (filter: TagFilter) => void;
    onRemoveTagFilter: (filter: TagFilter) => void;
    onSetTagLogic: (logic: 'AND' | 'OR') => void;
}

interface TagRowState {
    id: string;
    entityType: 'group' | 'team' | 'user';
}

// === Helper ===

const extractTags = (response: { groupTags: TagItem[]; teamTags: TagItem[]; userTags: TagItem[] }, entityType: 'group' | 'team' | 'user'): TagItem[] => {
    switch (entityType) {
        case 'group': return response.groupTags || [];
        case 'team': return response.teamTags || [];
        case 'user': return response.userTags || [];
    }
};

// === Tag Row Sub-Component ===

interface TagRowProps {
    entityType: 'group' | 'team' | 'user';
    usedEntityTypes: Set<string>;
    selectedFilters: TagFilter[];
    tagDetailsMap: Map<string, TagItem>;
    // True while the parent is still loading names for restored filters, so an
    // unnamed chip can say "Loading…" instead of asserting it's unresolvable.
    tagDetailsLoading: boolean;
    criteria: RecipientCriteria;
    onChangeEntityType: (entityType: 'group' | 'team' | 'user') => void;
    onSelectTag: (tag: TagItem, entityType: 'group' | 'team' | 'user') => void;
    onRemoveTag: (filter: TagFilter) => void;
    onTagDetailsLoaded: (tags: TagItem[]) => void;
    onRemoveRow?: () => void;
}

const TagRow: React.FC<TagRowProps> = ({
    entityType,
    usedEntityTypes,
    selectedFilters,
    tagDetailsMap,
    tagDetailsLoading,
    criteria,
    onChangeEntityType,
    onSelectTag,
    onRemoveTag,
    onTagDetailsLoaded,
    onRemoveRow,
}) => {
    const [query, setQuery] = useState('');
    const [availableTags, setAvailableTags] = useState<TagItem[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const debouncedQuery = useDebounce(query, 300);

    const fetchTags = () => {
        const hasSelection = criteria.selectAllUsers
            || criteria.groupIds?.length
            || criteria.teamIds?.length
            || criteria.userIds?.length;
        if (!hasSelection) {
            setAvailableTags([]);
            return;
        }
        setLoading(true);
        ComposeSearchService.fetchTags(criteria, entityType, debouncedQuery || undefined)
            .then((response) => {
                const tags = extractTags(response, entityType);
                setAvailableTags(tags);
                onTagDetailsLoaded(tags);
            })
            .catch(() => setAvailableTags([]))
            .finally(() => setLoading(false));
    };

    // Fetch tags when entity type or debounced query changes
    useEffect(() => {
        fetchTags();
    }, [entityType, debouncedQuery]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedTagIds = new Set(selectedFilters.map(f => f.tagId));
    const unselectedTags = availableTags.filter(tag => !selectedTagIds.has(tag.id));

    const handleSelect = (tag: TagItem) => {
        onSelectTag(tag, entityType);
        setQuery('');
        setShowDropdown(false);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }} ref={containerRef}>
            {/* Entity type selector */}
            <FormControl size="small" sx={{ minWidth: 140, mt: '4px' }}>
                <Select
                    value={entityType}
                    onChange={(e) => {
                        onChangeEntityType(e.target.value as 'group' | 'team' | 'user');
                        setQuery('');
                    }}
                    sx={{ fontSize: '0.875rem', borderRadius: 1 }}
                >
                    <MenuItem value="group" disabled={entityType !== 'group' && usedEntityTypes.has('group')}>Group Tags</MenuItem>
                    <MenuItem value="team" disabled={entityType !== 'team' && usedEntityTypes.has('team')}>Team Tags</MenuItem>
                    <MenuItem value="user" disabled={entityType !== 'user' && usedEntityTypes.has('user')}>User Tags</MenuItem>
                </Select>
            </FormControl>

            {/* Search/browse input with inline chips */}
            <Box sx={{ flex: 1, position: 'relative' }}>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: showDropdown ? GT_COLORS.primary : GT_COLORS.borderInput,
                        borderRadius: 1,
                        p: 0.75,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.5,
                        minHeight: 40,
                        cursor: 'text',
                        backgroundColor: 'white',
                        '&:hover': {
                            borderColor: showDropdown ? GT_COLORS.primary : GT_COLORS.textSecondary,
                        },
                    }}
                    onClick={() => inputRef.current?.focus()}
                >
                    {/* Selected tag chips */}
                    {selectedFilters.map((filter) => {
                        const tag = tagDetailsMap.get(filter.tagId);
                        return (
                            <Chip
                                key={filter.tagId}
                                label={displayName(tag?.name, tagDetailsLoading)}
                                size="small"
                                onDelete={() => onRemoveTag(filter)}
                                sx={{
                                    backgroundColor: tag?.color || '#757575',
                                    color: 'white',
                                    '& .MuiChip-deleteIcon': {
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        '&:hover': { color: 'white' },
                                    },
                                }}
                            />
                        );
                    })}

                    {/* Text input */}
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setShowDropdown(true);
                        }}
                        onFocus={() => {
                            fetchTags();
                            setShowDropdown(true);
                        }}
                        placeholder={selectedFilters.length === 0 ? 'Search tags or click to browse...' : ''}
                        style={{
                            border: 'none',
                            outline: 'none',
                            flex: 1,
                            minWidth: 120,
                            fontSize: '0.875rem',
                            backgroundColor: 'transparent',
                            padding: '4px 0',
                        }}
                    />
                </Box>

                {/* Dropdown */}
                {showDropdown && (
                    <Paper
                        elevation={4}
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 20,
                            maxHeight: 200,
                            overflow: 'auto',
                            mt: 0.5,
                            borderRadius: 2.5,
                            border: `1px solid ${GT_COLORS.border}`,
                        }}
                    >
                        {unselectedTags.length > 0 ? (
                            unselectedTags.map((tag) => (
                                <Box
                                    key={tag.id}
                                    onClick={() => handleSelect(tag)}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        '&:hover': { backgroundColor: GT_COLORS.hoverBg },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: tag.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textPrimary }}>{tag.name}</Typography>
                                </Box>
                            ))
                        ) : (
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted, fontStyle: 'italic' }}>
                                    {loading ? 'Loading tags...' : `No ${entityType} tags available based on selected recipients`}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                )}
            </Box>

            {/* Remove row button */}
            {onRemoveRow && (
                <IconButton size="small" onClick={onRemoveRow} sx={{ mt: '4px', color: GT_COLORS.textFaint }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};

// === Main Component ===

export const TagFilteringSection: React.FC<Props> = ({
    criteria,
    onAddTagFilter,
    onRemoveTagFilter,
    onSetTagLogic,
}) => {
    const [rows, setRows] = useState<TagRowState[]>(() => {
        const existingFilters = criteria.tagFilters || [];
        if (existingFilters.length === 0) {
            return [{ id: '1', entityType: 'group' }];
        }
        const entityTypes = Array.from(new Set(existingFilters.map(f => f.entityType)));
        return entityTypes.map((type, i) => ({ id: String(i + 1), entityType: type }));
    });
    const [tagDetailsMap, setTagDetailsMap] = useState<Map<string, TagItem>>(new Map());
    const [tagDetailsLoading, setTagDetailsLoading] = useState(false);

    const currentFilters = criteria.tagFilters || [];
    const tagLogic = criteria.tagLogic || 'AND';
    const hasSelection = criteria.selectAllUsers
        || !!criteria.groupIds?.length
        || !!criteria.teamIds?.length
        || !!criteria.userIds?.length;

    // Load tag details for pre-existing filters (edit mode). `criteria` is
    // populated asynchronously on the edit route, so with `[]` deps this hit the
    // early return on first render and never ran again — leaving every restored
    // tag chip permanently unnamed. Re-run when the filter set actually changes,
    // keyed on the ids so a response that doesn't include them can't loop.
    const fetchedTagsForKey = useRef<string>('');

    useEffect(() => {
        // Only fetch names we don't already have. Keying on the whole filter set
        // would refire this on every chip add/remove, and without tagEntityType
        // that is the most expensive variant of the endpoint.
        const missing = currentFilters
            .map(f => f.tagId)
            .filter(id => !tagDetailsMap.has(id));
        if (missing.length === 0) return;

        // Attempt each missing set once: the response may legitimately not
        // contain them, and retrying on that would loop.
        const key = [...missing].sort().join(',');
        if (fetchedTagsForKey.current === key) return;
        fetchedTagsForKey.current = key;

        setTagDetailsLoading(true);
        ComposeSearchService.fetchTags(criteria)
            .then((response) => {
                const allTags = [...(response.groupTags || []), ...(response.teamTags || []), ...(response.userTags || [])];
                setTagDetailsMap(prev => {
                    const newMap = new Map(prev);
                    allTags.forEach(tag => newMap.set(tag.id, tag));
                    return newMap;
                });
            })
            .catch((err) => {
                // Release the once-only key so a transient failure isn't permanent —
                // the next filter change retries rather than leaving these chips
                // unnamed for the rest of the session.
                fetchedTagsForKey.current = '';
                // Message only — the rejection carries the request config (auth
                // header, recipient criteria), which shouldn't reach the console.
                console.error('[TagFiltering] tag details fetch failed:', err?.message ?? String(err));
            })
            .finally(() => setTagDetailsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFilters, tagDetailsMap]);

    const handleAddRow = () => {
        const usedTypes = new Set(rows.map(r => r.entityType));
        let newType: 'group' | 'team' | 'user' = 'group';
        if (!usedTypes.has('team')) newType = 'team';
        else if (!usedTypes.has('user')) newType = 'user';

        setRows(prev => [...prev, { id: String(Date.now()), entityType: newType }]);
    };

    const handleRemoveRow = (rowId: string) => {
        const row = rows.find(r => r.id === rowId);
        if (row) {
            currentFilters
                .filter(f => f.entityType === row.entityType)
                .forEach(f => onRemoveTagFilter(f));
        }
        setRows(prev => prev.filter(r => r.id !== rowId));
    };

    const handleChangeEntityType = (rowId: string, entityType: 'group' | 'team' | 'user') => {
        const row = rows.find(r => r.id === rowId);
        if (row) {
            currentFilters
                .filter(f => f.entityType === row.entityType)
                .forEach(f => onRemoveTagFilter(f));
        }
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, entityType } : r));
    };

    const handleSelectTag = (tag: TagItem, entityType: 'group' | 'team' | 'user') => {
        setTagDetailsMap(prev => new Map(prev).set(tag.id, tag));
        onAddTagFilter({ entityType, tagId: tag.id });
    };

    const handleTagDetailsLoaded = (tags: TagItem[]) => {
        setTagDetailsMap(prev => {
            const newMap = new Map(prev);
            tags.forEach(tag => newMap.set(tag.id, tag));
            return newMap;
        });
    };

    const getSelectedTagNames = (): string => {
        return currentFilters
            .map(f => displayName(tagDetailsMap.get(f.tagId)?.name, tagDetailsLoading))
            .join(', ');
    };

    return (
        <Box sx={{
            backgroundColor: GT_COLORS.pageBg,
            borderRadius: 2.5,
            p: 3,
            mb: 3,
        }}>
            {/* Helper text — outside disabled wrapper so it stays fully visible */}
            {!hasSelection && (
                <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textPrimary, fontStyle: 'italic', mb: 2 }}>
                    Select recipients above to enable tag filtering
                </Typography>
            )}

            {/* All content — muted and non-interactive when no recipients selected */}
            <Box sx={{
                opacity: hasSelection ? 1 : 0.45,
                pointerEvents: hasSelection ? 'auto' : 'none',
                transition: 'opacity 0.2s ease',
            }}>
                {/* Header: Title + AND/OR toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary }}>
                        Filter by Tags (optional)
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted }}>
                            Tag Logic:
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            value={tagLogic}
                            exclusive
                            onChange={(_, value) => {
                                if (value) onSetTagLogic(value);
                            }}
                            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', borderRadius: 1 } }}
                        >
                            <ToggleButton value="AND" sx={{ px: 2 }}>AND</ToggleButton>
                            <ToggleButton value="OR" sx={{ px: 2 }}>OR</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
                {/* Description */}
                <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted, fontStyle: 'italic', mb: 3 }}>
                    {tagLogic === 'AND'
                        ? 'Tags filter your selection above \u2014 only recipients matching ALL selected tags will receive the message (AND logic)'
                        : 'Tags filter your selection above \u2014 recipients matching ANY selected tag will receive the message (OR logic)'
                    }
                </Typography>

                {/* Tag Rows */}
                {rows.map((row) => {
                    const rowFilters = currentFilters.filter(f => f.entityType === row.entityType);
                    const usedEntityTypes = new Set(rows.map(r => r.entityType));
                    return (
                        <TagRow
                            key={row.id}
                            entityType={row.entityType}
                            usedEntityTypes={usedEntityTypes}
                            selectedFilters={rowFilters}
                            tagDetailsMap={tagDetailsMap}
                            tagDetailsLoading={tagDetailsLoading}
                            criteria={criteria}
                            onChangeEntityType={(type) => handleChangeEntityType(row.id, type)}
                            onSelectTag={handleSelectTag}
                            onRemoveTag={onRemoveTagFilter}
                            onTagDetailsLoaded={handleTagDetailsLoaded}
                            onRemoveRow={() => handleRemoveRow(row.id)}
                        />
                    );
                })}

                {/* Add Another Row Button */}
                {rows.length < 3 && (
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddRow}
                        variant="outlined"
                        sx={{
                            mt: 1,
                            mb: 2,
                            borderStyle: 'dashed',
                            textTransform: 'none',
                            borderRadius: 1,
                            color: GT_COLORS.primary,
                            borderColor: GT_COLORS.borderInput,
                        }}
                    >
                        Add Another Tag Type
                    </Button>
                )}

                {/* Warning banner when tags are selected */}
                {currentFilters.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Tag filters applied: showing only recipients who match {tagLogic === 'AND' ? 'ALL' : 'ANY'} selected tags ({getSelectedTagNames()})
                    </Alert>
                )}
            </Box>
        </Box>
    );
};
